'use client';

import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/20/solid';
import { useQueryClient } from '@tanstack/react-query';
import { createContext, type ReactNode, useContext, useState } from 'react';
import { SendResetPasswordLinkForm } from '@/components/forms/SendResetPasswordLinkForm';
import { SignInForm } from '@/components/forms/SignInForm';
import { SignUpForm } from '@/components/forms/SignUpForm';
import { Logo } from '../components/logo';

type AuthDialogContextType = {
  isOpen: boolean;
  toggleIsOpen: (mode?: AuthMode) => void;
  changeMode: (mode: AuthMode) => void;
};

const SignUpModalContext = createContext<AuthDialogContextType | undefined>(
  undefined
);

type AuthMode = 'sign-up' | 'sign-in' | 'reset-password';

export const AuthDialogProvider = ({ children }: { children: ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState<AuthMode | null>(null);

  const queryClient = useQueryClient();

  const changeMode = (authMode: AuthMode) => {
    setMode(authMode);
  };

  const toggleIsOpen = async (authMode?: AuthMode) => {
    if (!isOpen && authMode) {
      setIsOpen(true);
      setMode(authMode);
    } else {
      setIsOpen(false);
      setMode(null);
      await queryClient.invalidateQueries();
    }
  };

  return (
    <SignUpModalContext.Provider value={{ isOpen, toggleIsOpen, changeMode }}>
      {children}
      <Dialog
        className="relative z-50"
        onClose={() => setIsOpen(false)}
        open={isOpen}
      >
        {/* The backdrop, rendered as a fixed sibling to the panel container */}
        <DialogBackdrop className="fixed inset-0 bg-black/80" />

        {/* Full-screen scrollable container */}
        <div className="fixed inset-0 flex w-screen items-center justify-center p-4">
          <DialogPanel
            className="rounded-lg bg-light-gray p-6 text-left align-middle shadow-xl transition duration-300 ease-out data-closed:scale-95 data-closed:opacity-0 md:p-12"
            transition
          >
            <div className="mb-5 flex items-center justify-between">
              <Logo />
              <button
                aria-label="Close dialog"
                className="text-white transition-opacity hover:opacity-80"
                onClick={() => setIsOpen(false)}
                type="button"
              >
                <XMarkIcon aria-hidden="true" className="size-6" />
              </button>
            </div>
            {mode === 'sign-in' && <SignInForm />}
            {mode === 'sign-up' && <SignUpForm />}
            {mode === 'reset-password' && <SendResetPasswordLinkForm />}
          </DialogPanel>
        </div>
      </Dialog>
    </SignUpModalContext.Provider>
  );
};

export const useAuthDialog = () => {
  const context = useContext(SignUpModalContext);
  if (!context) {
    throw new Error('useAuthDialog must be used within a AuthDialogProvider');
  }
  return context;
};
